import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { NoteInput, ProductType, Schedule, ScheduleInput } from '@mfe/shared/schema-types';

type ScheduleInstallationState = {
  installDate: string;
  loading: boolean;
  submitLoading: boolean;
  submitSuccess: boolean;
  error: null | boolean | string;
  externalWorkOrderId: string;
  fulfillmentAgreementId: string;
  products?: ProductType[];
  availableDates: Record<string, ScheduleInput[]>;
  fiber: {
    isServiceable: boolean;
    estimatedServiceableDate: string | null;
    serviceableMessage: string | null;
  };
};

export const initialScheduleInstallationState = {
  installDate: null,
  loading: true,
  submitLoading: false,
  submitSuccess: false,
  error: null,
  externalWorkOrderId: null,
  fulfillmentAgreementId: null,
  availableDates: null,
  products: [],
  fiber: {
    isServiceable: null,
    estimatedServiceableDate: null,
    serviceableMessage: null,
  },
};

type InstallationOrderInput = {
  externalWorkOrderId: string;
  note?: NoteInput;
  schedule: Schedule;
};

export type SubmitInstallationActionType = PayloadAction<InstallationOrderInput>;

export const scheduleInstallationSlice = createSlice({
  name: 'scheduleInstallation',
  initialState: initialScheduleInstallationState,
  reducers: {
    // for change plan flow
    getAvailableDates: (state, action: { payload: { cartId: string; refetch: boolean } }) => {
      state.loading = true;
      state.error = null;
    },
    // for standalone flow
    getInstallationWorkOrder: (state) => {
      state.loading = true;
      state.error = null;
    },
    setWorkOrderDetails: (state, action) => {
      const { fulfillmentAgreementId, products } = action.payload;
      return {
        ...state,
        fulfillmentAgreementId,
        products,
      };
    },
    setExternalWorkOrderId: (state, { payload }) => {
      state.externalWorkOrderId = payload;
    },
    setAvailableDates: (state, { payload }) => {
      state.loading = false;
      state.error = null;
      state.availableDates = payload;
    },
    setIsServiceable: (state, { payload }) => {
      state.fiber.isServiceable = payload;
      state.loading = false;
    },
    setEstimatedServiceableDate: (state, { payload }) => {
      state.fiber.estimatedServiceableDate = payload;
    },
    setServiceableMessage: (state, { payload }) => {
      state.fiber.serviceableMessage = payload;
      state.loading = false;
    },

    refetchScheduleInstallation: (state) => {
      state.loading = true;
      state.error = null;
      state.submitSuccess = false;
    },
    submitInstallation: (state, action: SubmitInstallationActionType) => {
      state.submitLoading = true;
    },
    setScheduleSuccess: (state) => {
      state.submitLoading = false;
      state.submitSuccess = true;
      state.error = null;
    },
    setError: (state, { payload }) => {
      state.error = payload;
      state.submitLoading = false;
    },
    resetScheduleInstallation: () => initialScheduleInstallationState,
  },
});

export const {
  getAvailableDates, // for change plan only
  getInstallationWorkOrder,
  setWorkOrderDetails,
  setIsServiceable,
  setEstimatedServiceableDate,
  setServiceableMessage,
  setExternalWorkOrderId,
  setAvailableDates,
  refetchScheduleInstallation,
  submitInstallation,
  setScheduleSuccess,
  setError: setScheduleInstallationError,
  resetScheduleInstallation,
} = scheduleInstallationSlice.actions;

export const selectScheduleInstallation = (state: { scheduleInstallation: ScheduleInstallationState }) =>
  state.scheduleInstallation;

export const selectIsFiberServiceable = (state: { scheduleInstallation: ScheduleInstallationState }) =>
  state.scheduleInstallation.fiber.isServiceable;

export const selectFiberServiceableMessage = (state: { scheduleInstallation: ScheduleInstallationState }) =>
  state.scheduleInstallation.fiber.serviceableMessage;

export const selectScheduleInstallationError = (state: { scheduleInstallation: ScheduleInstallationState }) =>
  state.scheduleInstallation.error;
